import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Section, Heading, Spinner, PlantBadge } from "../components/Bits";
import { Trophy, Target, Zap, CheckCircle2, XCircle, ArrowRight, Home as HomeIcon, RotateCcw } from "lucide-react";

const DIFFICULTIES = [
  { key: "beginner", label: "Einsteiger", desc: "Nur essbare Klassiker", color: "#5a6e3f" },
  { key: "intermediate", label: "Fortgeschritten", desc: "Heilkräuter + essbare Wildpflanzen", color: "#c58e2f" },
  { key: "pro", label: "Profi", desc: "Alle Pflanzen inkl. giftige Doppelgänger", color: "#b94a48" },
];

const MODES = [
  { key: "classic", label: "Klassisch (10 Fragen)", icon: Target, desc: "10 Fragen – dein Score am Ende" },
  { key: "endless", label: "Endlos-Modus", icon: Zap, desc: "So weit du kommst – bei falsch Schluss" },
];

export default function Quiz() {
  const { user } = useAuth() || {};
  const navigate = useNavigate();
  const [stage, setStage] = useState("start"); // start | play | done
  const [config, setConfig] = useState({ mode: "classic", difficulty: "beginner" });
  const [session, setSession] = useState(null);
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/quiz/my-stats").then((r) => setStats(r.data)).catch(() => {});
  }, [user, stage]);

  const start = async () => {
    setLoading(true);
    try {
      const r = await api.post("/quiz/start", config);
      setSession(r.data);
      setFeedback(null);
      setResult(null);
      setStage("play");
      await loadNext(r.data.session_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Starten");
    } finally {
      setLoading(false);
    }
  };

  const loadNext = async (sid) => {
    setLoading(true);
    try {
      const r = await api.get(`/quiz/next/${sid || session.session_id}`);
      setQuestion(r.data);
      setFeedback(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler");
    } finally {
      setLoading(false);
    }
  };

  const answer = async (chosen_slug) => {
    if (!session || !question || feedback) return;
    try {
      const r = await api.post("/quiz/answer", {
        session_id: session.session_id,
        question_id: question.question_id,
        chosen_slug,
      });
      setFeedback(r.data);
      // Persist tally into session so HUD stays correct after next-question reset
      if (r.data?.session) {
        setSession((prev) => ({ ...prev, ...r.data.session }));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler");
    }
  };

  const nextOrFinish = async () => {
    if (!feedback) return;
    if (feedback.auto_finished) await finish();
    else await loadNext();
  };

  const finish = async () => {
    try {
      const r = await api.post("/quiz/finish", { session_id: session.session_id });
      setResult(r.data);
      setStage("done");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Beenden");
    }
  };

  if (!user) {
    return (
      <Section>
        <div className="max-w-md mx-auto text-center py-16" data-testid="quiz-login-gate">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-5" style={{ backgroundColor: "var(--wp-olive)", color: "var(--wp-cream)" }}>
            <Trophy className="w-8 h-8" />
          </div>
          <h1 className="font-display text-4xl mb-3" style={{ color: "var(--wp-olive)" }}>Pflanzen-Quiz</h1>
          <p className="opacity-80 mb-6">Melde dich an, um dein Wissen zu testen und Punkte zu sammeln.</p>
          <button onClick={() => navigate("/login")} className="btn-primary" data-testid="quiz-login-btn">Anmelden</button>
        </div>
      </Section>
    );
  }

  if (stage === "start") {
    return (
      <Section>
        <Heading overline="Wissen testen" title="Pflanzen-Quiz" subtitle="Wie gut kennst du die Wildpflanzen Luxemburgs?" />

        {stats && stats.total_played > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8 max-w-3xl" data-testid="quiz-my-stats">
            <StatTile icon={<Trophy className="w-4 h-4" />} label="Runden" value={stats.total_played} />
            <StatTile icon={<CheckCircle2 className="w-4 h-4" />} label="Richtig" value={stats.total_correct} />
            <StatTile icon={<Target className="w-4 h-4" />} label="Trefferquote" value={stats.total_correct + stats.total_wrong > 0 ? `${Math.round(stats.total_correct / (stats.total_correct + stats.total_wrong) * 100)}%` : "–"} />
            <StatTile icon={<Zap className="w-4 h-4" />} label="Bester Score" value={Math.max(0, ...Object.values(stats.best_by_difficulty || {}))} />
          </div>
        )}

        <div className="max-w-3xl">
          <div className="overline mb-2">Modus</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            {MODES.map((m) => {
              const Ico = m.icon;
              const active = config.mode === m.key;
              return (
                <button key={m.key} onClick={() => setConfig({ ...config, mode: m.key })} className="plant-card p-4 text-left transition-all"
                  style={{ borderColor: active ? "var(--wp-olive)" : "var(--wp-border)", borderWidth: 2 }} data-testid={`quiz-mode-${m.key}`}>
                  <div className="flex items-center gap-3 mb-1">
                    <Ico className="w-5 h-5" style={{ color: "var(--wp-olive)" }} />
                    <span className="font-medium">{m.label}</span>
                  </div>
                  <div className="text-xs opacity-70">{m.desc}</div>
                </button>
              );
            })}
          </div>

          <div className="overline mb-2">Schwierigkeit</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
            {DIFFICULTIES.map((d) => {
              const active = config.difficulty === d.key;
              return (
                <button key={d.key} onClick={() => setConfig({ ...config, difficulty: d.key })} className="plant-card p-4 text-left transition-all"
                  style={{ borderColor: active ? d.color : "var(--wp-border)", borderWidth: 2 }} data-testid={`quiz-difficulty-${d.key}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium">{d.label}</span>
                    {active && <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />}
                  </div>
                  <div className="text-xs opacity-70">{d.desc}</div>
                </button>
              );
            })}
          </div>

          <button onClick={start} disabled={loading} className="btn-primary text-base w-full sm:w-auto justify-center" data-testid="quiz-start-btn">
            {loading ? <Spinner /> : <><Zap className="w-5 h-5" /> Quiz starten</>}
          </button>
        </div>
      </Section>
    );
  }

  if (stage === "play") {
    const answered = (session?.correct ?? 0) + (session?.wrong ?? 0);
    const target = session?.questions_target;
    const progress = target ? Math.min(100, Math.round((answered / target) * 100)) : 0;
    return (
      <Section>
        <div className="flex items-center justify-between gap-3 mb-5 max-w-3xl mx-auto">
          <button onClick={() => { if (window.confirm("Aktuelle Runde abbrechen und zurück?")) setStage("start"); }} className="text-xs opacity-70 hover:opacity-100">← Abbrechen</button>
          <div className="flex items-center gap-4 text-xs">
            <span data-testid="quiz-hud-correct"><CheckCircle2 className="w-3.5 h-3.5 inline" style={{ color: "var(--wp-primary)" }} /> {session?.correct ?? 0}</span>
            <span data-testid="quiz-hud-wrong"><XCircle className="w-3.5 h-3.5 inline" style={{ color: "var(--wp-toxic)" }} /> {session?.wrong ?? 0}</span>
            <span data-testid="quiz-hud-score"><Trophy className="w-3.5 h-3.5 inline" /> {session?.score ?? 0}</span>
            {target && <span className="opacity-70">{answered}/{target}</span>}
          </div>
        </div>
        {target && (
          <div className="max-w-3xl mx-auto mb-6 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: "var(--wp-cream)" }}>
            <div className="h-full transition-all duration-500" style={{ width: `${progress}%`, backgroundColor: "var(--wp-olive)" }} />
          </div>
        )}
        {loading || !question ? <Spinner /> : (
          <QuestionCard question={question} feedback={feedback} onAnswer={answer} onNext={nextOrFinish} mode={session?.mode} />
        )}
      </Section>
    );
  }

  return (
    <Section>
      <div className="max-w-2xl mx-auto text-center py-6" data-testid="quiz-result">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4"
             style={{ backgroundColor: result?.is_new_best ? "var(--wp-primary)" : "var(--wp-olive)", color: "var(--wp-cream)" }}>
          <Trophy className="w-10 h-10" />
        </div>
        <div className="overline mb-2">Ergebnis</div>
        <h1 className="font-display text-5xl mb-2" style={{ color: "var(--wp-olive)" }} data-testid="quiz-result-score">{result?.score ?? 0}</h1>
        <div className="text-sm opacity-70 mb-6">Punkte · {result?.correct} richtig · {result?.wrong} falsch · {result?.accuracy}% Trefferquote</div>
        {result?.is_new_best && (
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 text-sm font-medium"
               style={{ backgroundColor: "var(--wp-primary)", color: "var(--wp-cream)" }} data-testid="quiz-new-best">
            <Trophy className="w-4 h-4" /> Neuer Bestwert!
          </div>
        )}
        {!result?.is_new_best && result?.previous_best > 0 && (
          <div className="text-xs opacity-60 mb-6">Bester Score bisher: {result.previous_best}</div>
        )}
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <button onClick={() => { setStage("start"); setResult(null); setSession(null); }} className="btn-primary" data-testid="quiz-again">
            <RotateCcw className="w-4 h-4" /> Nochmal spielen
          </button>
          <Link to="/plants" className="btn-secondary">
            <HomeIcon className="w-4 h-4" /> Pflanzen entdecken
          </Link>
        </div>
      </div>
    </Section>
  );
}

function StatTile({ icon, label, value }) {
  return (
    <div className="plant-card p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-60 mb-1">{icon} {label}</div>
      <div className="text-2xl font-medium tabular-nums" style={{ color: "var(--wp-olive)" }}>{value}</div>
    </div>
  );
}

function QuestionCard({ question, feedback, onAnswer, onNext, mode }) {
  const isImgAsk = question.format === "name_from_image";
  return (
    <div className="max-w-3xl mx-auto plant-card overflow-hidden" data-testid="quiz-question">
      <div className="p-5 sm:p-6 border-b" style={{ borderColor: "var(--wp-border)" }}>
        <div className="overline mb-1.5">{isImgAsk ? "Welche Pflanze ist das?" : "Welches Bild zeigt …"}</div>
        {isImgAsk ? (
          question.plant.image_url && (
            <div className="aspect-[16/10] overflow-hidden rounded-xl">
              <img src={question.plant.image_url} alt="Pflanze" className="w-full h-full object-cover" data-testid="quiz-question-image" />
            </div>
          )
        ) : (
          <div className="font-display text-3xl sm:text-4xl leading-tight" style={{ color: "var(--wp-olive)" }} data-testid="quiz-question-name">
            {question.plant.name_de}
          </div>
        )}
      </div>

      <div className={`p-4 sm:p-5 grid gap-2.5 ${isImgAsk ? "grid-cols-1" : "grid-cols-2"}`}>
        {question.options.map((opt) => {
          const isChosen = feedback?.chosen_slug === opt.slug;
          const isCorrect = feedback?.correct_plant?.slug === opt.slug;
          const showResult = !!feedback;
          const style = !showResult
            ? { borderColor: "var(--wp-border)" }
            : isCorrect
              ? { borderColor: "var(--wp-primary)", backgroundColor: "rgba(90,110,63,0.08)", borderWidth: 2 }
              : isChosen
                ? { borderColor: "var(--wp-toxic)", backgroundColor: "rgba(185,74,72,0.08)", borderWidth: 2, opacity: 0.85 }
                : { borderColor: "var(--wp-border)", opacity: 0.5 };
          return (
            <button key={opt.slug} disabled={showResult} onClick={() => onAnswer(opt.slug)}
                    className="p-3 rounded-xl border text-left transition-all hover:bg-[var(--wp-cream)] disabled:cursor-default disabled:hover:bg-transparent"
                    style={style} data-testid={`quiz-option-${opt.slug}`}>
              {isImgAsk ? (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">{opt.name_de}</span>
                  {showResult && isCorrect && <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: "var(--wp-primary)" }} />}
                  {showResult && isChosen && !isCorrect && <XCircle className="w-4 h-4 shrink-0" style={{ color: "var(--wp-toxic)" }} />}
                </div>
              ) : (
                <div>
                  <div className="aspect-[4/3] overflow-hidden rounded-lg mb-1.5">
                    {opt.image_url && <img src={opt.image_url} alt={opt.name_de} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <span className="truncate">{showResult ? opt.name_de : ""}</span>
                    {showResult && isCorrect && <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: "var(--wp-primary)" }} />}
                    {showResult && isChosen && !isCorrect && <XCircle className="w-4 h-4 shrink-0" style={{ color: "var(--wp-toxic)" }} />}
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {feedback && (
        <div className="p-4 sm:p-5 border-t" style={{ borderColor: "var(--wp-border)", backgroundColor: "var(--wp-cream)" }} data-testid="quiz-feedback">
          <div className="flex items-start gap-3">
            {feedback.correct ? <CheckCircle2 className="w-6 h-6 shrink-0" style={{ color: "var(--wp-primary)" }} /> : <XCircle className="w-6 h-6 shrink-0" style={{ color: "var(--wp-toxic)" }} />}
            <div className="flex-1 min-w-0">
              <div className="font-medium mb-1">{feedback.correct ? "Richtig!" : "Leider falsch"}</div>
              <div className="text-sm opacity-80 mb-2">
                {feedback.correct_plant?.name_de} <span className="italic opacity-70">{feedback.correct_plant?.name_lat && `(${feedback.correct_plant.name_lat})`}</span>
                {feedback.correct_plant?.category && <span className="ml-2 inline-flex"><PlantBadge category={feedback.correct_plant.category} /></span>}
              </div>
              {feedback.correct_plant?.short_description && (
                <p className="text-xs opacity-70 mb-2 line-clamp-3">{feedback.correct_plant.short_description}</p>
              )}
              <Link to={`/plants/${feedback.correct_plant?.slug}`} className="text-xs underline" style={{ color: "var(--wp-olive)" }} data-testid="quiz-plant-link">
                Zum Pflanzen-Profil →
              </Link>
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <button onClick={onNext} className="btn-primary !py-2 !px-4 text-sm" data-testid="quiz-next-btn">
              {feedback.auto_finished ? "Ergebnis anzeigen" : "Nächste Frage"} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
